import { InvalidCredentialsError, ServerError, UnexpectedError } from '@/domain/errors'
import { AppError } from '@/domain/errors/app-error'
import { BadRequestError } from '@/domain/errors/bad-request-error'
import { ConflictError } from '@/domain/errors/conflict-error'
import { ForbiddenError } from '@/domain/errors/forbidden-error'
import { NotFoundError } from '@/domain/errors/not-found-error'
import { GenericResponse } from '@/infra/http'
import { HttpResponse, HttpStatusCode } from '../protocols/http'

export type HttpResponseErrorInstance = new (message?: string | null) => Error

export interface HttpResponseErrorFunction {
  <T>(body: GenericResponse<T>): Error
}

export interface HttpResponseErrorMap {
  [key: number]: HttpResponseErrorFunction | HttpResponseErrorInstance
}

export abstract class BaseRemote {
  private static readonly ERROR_MAP: HttpResponseErrorMap = {
    [HttpStatusCode.badRequest]: body => {
      return new BadRequestError(body?.message, body?.data as BadRequestError.Model)
    },
    [HttpStatusCode.forbidden]: ForbiddenError,
    [HttpStatusCode.unauthorized]: InvalidCredentialsError,
    [HttpStatusCode.notFound]: NotFoundError,
    [HttpStatusCode.conflict]: ConflictError,
    [HttpStatusCode.serverError]: ServerError
  }

  protected isKnownError(error: HttpResponseErrorFunction | HttpResponseErrorInstance): boolean {
    return Object.values(BaseRemote.ERROR_MAP).some(
      fe =>
        fe.name === error.name &&
        fe.prototype &&
        (fe.prototype instanceof AppError || fe.prototype instanceof Error)
    )
  }

  isOk = (statusCode: number) =>
    statusCode >= HttpStatusCode.ok && statusCode < HttpStatusCode.multipleChoices

  onSuccess<T, R>(
    response: HttpResponse<GenericResponse<T>>,
    callBack?: (data: GenericResponse<T>) => R,
    catchError: HttpResponseErrorMap = {}
  ): R {
    if (this.isOk(response.statusCode)) {
      if (!response.body?.success) {
        throw new UnexpectedError(response.body?.message)
      }
      return callBack ? callBack(response.body!) : (response.body! as R)
    }

    const error = { ...BaseRemote.ERROR_MAP, ...catchError }[response.statusCode]
    if (!error) throw new UnexpectedError(response.body?.message)
    throw this.isKnownError(error)
      ? new (error as HttpResponseErrorInstance)(response.body?.message)
      : (error as HttpResponseErrorFunction)(response.body!)
  }

  onSuccessData<T, R>(
    response: HttpResponse<GenericResponse<T>>,
    callBack?: (data: GenericResponse<T>) => R,
    catchError?: HttpResponseErrorMap
  ): R {
    return this.onSuccess(
      response,
      (body: GenericResponse<T>) => {
        if (body?.data === null) {
          throw new UnexpectedError()
        }
        return callBack ? callBack(body) : (body as R)
      },
      catchError
    )
  }

  onSuccessResponseData<T, R extends { data: T }>(
    response: HttpResponse<GenericResponse<T>>,
    callBack?: (data: T) => R,
    catchError?: HttpResponseErrorMap
  ): T {
    const body = this.onSuccessData(response, this.overrideCallback(callBack), catchError)
    return body!.data! as T
  }

  private overrideCallback<T, R>(
    callBack?: (data: T) => R
  ): ((data: GenericResponse<T>) => R) | undefined {
    return callBack ? (data: GenericResponse<T>) => callBack(data.data! as T) : undefined
  }
}
